import { useCallback, useMemo, useState } from "react";

/** Minimal musician type for the select dropdown */
export type Musician = {
  id: string;
  name: string | null;
};

type Props = {
  musicians: Musician[];
  value: string | null;
  onChange: (value: string | null) => void;
  onCreateMusician: (name: string, email?: string) => Promise<Musician>;
  disabled?: boolean;
  name?: string;
};

export function AuthorSelectView({
  musicians,
  value,
  onChange,
  onCreateMusician,
  disabled,
  name = "authorId",
}: Props) {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdMusicians, setCreatedMusicians] = useState<Musician[]>([]);

  // Combine prop musicians with any locally created ones (deduped)
  const localMusicians = useMemo(() => {
    const propIds = new Set(musicians.map((m) => m.id));
    return [
      ...musicians,
      ...createdMusicians.filter((m) => !propIds.has(m.id)),
    ];
  }, [musicians, createdMusicians]);

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = e.target.value;
      if (selectedValue === "__add_new__") {
        setIsAddingNew(true);
        setError(null);
      } else {
        onChange(selectedValue || null);
      }
    },
    [onChange],
  );

  const handleAddNew = useCallback(async () => {
    if (!newName.trim()) {
      setError("Name is required");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const musician = await onCreateMusician(newName, newEmail || undefined);
      setCreatedMusicians((prev) => [...prev, musician]);
      onChange(musician.id);
      setIsAddingNew(false);
      setNewName("");
      setNewEmail("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add musician");
    } finally {
      setIsSubmitting(false);
    }
  }, [newName, newEmail, onCreateMusician, onChange]);

  const handleCancelAdd = useCallback(() => {
    setIsAddingNew(false);
    setNewName("");
    setNewEmail("");
    setError(null);
  }, []);

  if (isAddingNew) {
    return (
      <div className="rounded-md border border-amber bg-bg-secondary p-3">
        <div className="mb-3">
          <label
            htmlFor="new-author-name"
            className="mb-1 block text-xs text-text-muted"
          >
            Name *
          </label>
          <input
            id="new-author-name"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={isSubmitting}
            placeholder="Enter name"
            className="w-full rounded-md border border-border-subtle bg-bg-tertiary px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:border-amber focus:outline-none disabled:opacity-50"
          />
        </div>
        <div className="mb-3">
          <label
            htmlFor="new-author-email"
            className="mb-1 block text-xs text-text-muted"
          >
            Email (optional, for auto-linking when they sign up)
          </label>
          <input
            id="new-author-email"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={isSubmitting}
            placeholder="email@example.com"
            className="w-full rounded-md border border-border-subtle bg-bg-tertiary px-3 py-2 text-sm text-text-primary placeholder-text-muted focus:border-amber focus:outline-none disabled:opacity-50"
          />
        </div>
        {error && <p className="mb-3 text-sm text-red-muted">{error}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleAddNew}
            disabled={isSubmitting}
            className="rounded-md bg-amber px-3 py-1.5 text-sm font-medium text-on-accent transition-colors hover:bg-amber-warm disabled:opacity-50"
          >
            {isSubmitting ? "Adding..." : "Add"}
          </button>
          <button
            type="button"
            onClick={handleCancelAdd}
            disabled={isSubmitting}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-muted transition-colors hover:bg-bg-tertiary disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <select
      id="authorId"
      name={name}
      value={value ?? ""}
      onChange={handleSelectChange}
      disabled={disabled}
      className="w-full rounded-md border border-border-subtle bg-bg-tertiary px-3 py-2.5 text-sm text-text-primary transition-colors focus:border-amber focus:bg-bg-secondary disabled:opacity-50"
    >
      <option value="">No author</option>
      {localMusicians.map((musician) => (
        <option key={musician.id} value={musician.id}>
          {musician.name}
        </option>
      ))}
      <option value="__add_new__">+ Add New Author...</option>
    </select>
  );
}
